import { Fragment, ReactNode } from "react";
import { ImpactSummaryDataType } from "../model";

export type MakeActiveImpactCounts = {
  deletedActivities: number;
  updatedActivities: number;
  addedActivities: number;
  deletedMaterials: number;
  deletedSubmittals: number;
  updatedMaterials: number;
  updatedSubmittals: number;
};

type ProcurementNoun = {
  singular: "Material" | "Submittal";
  plural: "Materials" | "Submittals";
};

const MATERIAL_NOUN: ProcurementNoun = {
  singular: "Material",
  plural: "Materials"
};

const SUBMITTAL_NOUN: ProcurementNoun = {
  singular: "Submittal",
  plural: "Submittals"
};

export function getMakeActiveImpactCounts(
  summary: ImpactSummaryDataType
): MakeActiveImpactCounts {
  return {
    deletedActivities: Number(summary?.totalNoOfActivities?.deleted || 0),
    updatedActivities: Number(summary?.totalNoOfActivities?.updated || 0),
    addedActivities: Number(summary?.totalNoOfActivities?.added || 0),
    deletedMaterials: Number(summary?.impactedMaterials?.deleted || 0),
    deletedSubmittals: Number(summary?.impactedSubmittals?.deleted || 0),
    updatedMaterials: Number(summary?.impactedMaterials?.updated || 0),
    updatedSubmittals: Number(summary?.impactedSubmittals?.updated || 0)
  };
}

/** Show the impact layout whenever the new schedule has activity changes. */
export function hasReviewChangeImpact(counts: MakeActiveImpactCounts): boolean {
  return (
    counts.deletedActivities > 0 ||
    counts.updatedActivities > 0 ||
    counts.addedActivities > 0
  );
}

function SemiboldProcurementPhrase({
  count,
  noun
}: {
  count: number;
  noun: ProcurementNoun;
}) {
  return (
    <span className="font-semibold">
      {count} {count === 1 ? noun.singular : noun.plural}
    </span>
  );
}

function joinWithAnd(nodes: ReactNode[]): ReactNode {
  return nodes.map((node, index) => (
    <Fragment key={index}>
      {index > 0 ? " and " : null}
      {node}
    </Fragment>
  ));
}

function buildProcurementPhrases(
  materialCount: number,
  submittalCount: number
): ReactNode[] {
  const phrases: ReactNode[] = [];

  if (materialCount > 0) {
    phrases.push(
      <SemiboldProcurementPhrase
        key="material"
        count={materialCount}
        noun={MATERIAL_NOUN}
      />
    );
  }
  if (submittalCount > 0) {
    phrases.push(
      <SemiboldProcurementPhrase
        key="submittal"
        count={submittalCount}
        noun={SUBMITTAL_NOUN}
      />
    );
  }

  return phrases;
}

export function formatDeletedLinkedMessage(
  deletedMaterials: number,
  deletedSubmittals: number
): ReactNode {
  if (deletedMaterials + deletedSubmittals === 0) {
    return (
      <span data-testid="make-active-deleted-linked-message">
        No impact - no Materials or Submittals are linked.
      </span>
    );
  }

  const messageBody =
    deletedMaterials + deletedSubmittals === 1 ? (
      deletedMaterials > 0 ? (
        <>
          <span className="font-semibold">1 Material</span> is linked to deleted
          Activities and may lose its deadline.
        </>
      ) : (
        <>
          <span className="font-semibold">1 Submittal</span> is linked to
          deleted Activities and may lose its deadline.
        </>
      )
    ) : (
      <>
        {joinWithAnd(
          buildProcurementPhrases(deletedMaterials, deletedSubmittals)
        )}{" "}
        are linked to deleted Activities and may lose their deadline.
      </>
    );

  return (
    <div
      className="rounded border border-solid border-ciq-warning-border bg-ciq-warning-bg px-3 py-2"
      data-testid="make-active-deleted-linked-message"
    >
      <p className="mb-0 text-xs text-black text-opacity-65">{messageBody}</p>
    </div>
  );
}

export function formatUpdatedDeadlinesMessage(
  updatedMaterials: number,
  updatedSubmittals: number
): ReactNode {
  if (updatedMaterials + updatedSubmittals === 0) {
    return (
      <span data-testid="make-active-updated-deadlines-message">
        No impact - no Materials or Submittals are linked.
      </span>
    );
  }

  if (updatedMaterials + updatedSubmittals === 1) {
    if (updatedMaterials === 1) {
      return (
        <span data-testid="make-active-updated-deadlines-message">
          <SemiboldProcurementPhrase count={1} noun={MATERIAL_NOUN} /> will have
          an updated deadline.
        </span>
      );
    }

    return (
      <span data-testid="make-active-updated-deadlines-message">
        <SemiboldProcurementPhrase count={1} noun={SUBMITTAL_NOUN} /> will have
        an updated deadline.
      </span>
    );
  }

  return (
    <span data-testid="make-active-updated-deadlines-message">
      {joinWithAnd(
        buildProcurementPhrases(updatedMaterials, updatedSubmittals)
      )}{" "}
      will have updated deadlines.
    </span>
  );
}

export const ADDED_ACTIVITIES_CARD_MESSAGE = "";
